import styled from '@emotion/styled';
import {
  Category,
  Content,
  ticketInfoType,
  fetchCinemaOnline,
  getCompactConditionalProp,
  CompactConditionalProps,
} from 'common/apiService';
import HtmlParser from 'common/HtmlParser';
import { __t } from 'common/i18n';
import { IconInfo, IconPlay } from 'common/icons';
import Layout from 'common/Layout/Layout';
import { Box, Button, Card, CardBody, Ellipsis, Flex, Icon, Link, Paper, Text, Spinner } from 'common/styles';
import { getContentUrl, inspectFlags, sanitize } from 'common/utils';
import { ElementType, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useUser } from 'common/CacheProvider';
import CinemaLandingHeaderCover from 'common/CinemaLandingHeaderCover';
import { ButtonVariant } from 'common/styles/Button';
import xIPCountry from 'common/apiService/xIPCountry';
import * as Sentry from '@sentry/nextjs';
import Seo from 'common/Seo';
import Breadcrumb from 'common/Breadcrumb';
import lscache from 'lscache';

const CinemaOnlinePage = ({ videos, title, body, cover_image_file }: Category) => {
  const cover = cover_image_file?.path || '/static/images/landing/cinema-online/cover.jpg';
  const { full: bodyText } = sanitize({ raw: body || '', textOnly: true });
  const seoTitle = `${title || __t('cinema-online.title')} | ${__t('static_siteName')}`;
  const seoDescription = bodyText?.substring(0, 155) || 'تماشای فیلم‌های سینمایی در فیلمنت؛ خرید بلیت آنلاین و پخش انحصاری آثار سینمایی روی موبایل، تلویزیون و لپ‌تاپ.';

  const { loaded, authorized } = useUser();
  const [conditionalProps, setConditionalProps] = useState<CompactConditionalProps[]>([]);
  const [isTapsiSSO, setIsTapsiSSO] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsTapsiSSO(lscache.get('utm_source_sso') === 'tapsi');
    }
  }, []);

  useEffect(() => {
    const ids = videos?.map((video) => video.short_id).filter(Boolean) ?? [];
    if (!authorized || !loaded || !ids.length) return;
    (async () => {
      try {
        setLoading(true);
        const { data } = await getCompactConditionalProp(ids);
        setConditionalProps(data ?? []);
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorized, loaded]);

  return (
    <>
      <Seo
        title={seoTitle}
        description={seoDescription}
        path="/cinema-online"
        image={cover_image_file?.path}
      />
      <Breadcrumb items={[
        { name: __t('common.home'), url: '/' },
        { name: __t('cinema-online.title'), url: '/cinema-online' },
      ]} />
      <CinemaLandingHeaderCover image={cover} />
      <Layout disableTopGutter>
        <CinemaContentSummary>
          <Text variant="h2" as="h1" weight="medium" mb={3} style={{ zIndex: 1 }}>
            {title || __t('content.summary')}
          </Text>
          {body ? <div className="html-summary"><HtmlParser data={body} /></div> : ''}
        </CinemaContentSummary>
        {videos && videos?.length > 0 ? videos?.map((c) => {
          const conditional = conditionalProps.find((p) => p.short_id === c.short_id);
          return (
            <CinemaItem
              key={c.id}
              {...c}
              conditions={conditional?.conditional_flag ?? null}
              userTicketInfo={conditional?.ticket_info}
              isTapsiSSO={isTapsiSSO}
              loading={loading}
            />
          );
        }) : <Text variant="body2" weight="medium" pb={2}>
          در حال حاضر محتوایی موجود نیست
        </Text>}
      </Layout>
    </>
  );
};

const CinemaContentSummary = styled.div(() => ({
  zIndex: 1,
  position: 'relative',
  marginBottom: '10rem',
  '@media (max-width: 600px)': {
    marginBottom: '2.5rem',
    '> .html-summary': {
      display: 'none',
    },
  },
}));

type CinemaItemProps = Content & {
  conditions: string | null;
  userTicketInfo?: ticketInfoType;
  isTapsiSSO?: boolean;
  loading?: boolean;
};

const CinemaItem = ({
  title, cover_image, ticket_info, summary, short_id, slug,
  artists, flag, coming_soon,
  conditions, userTicketInfo, isTapsiSSO, loading,
}: CinemaItemProps) => {
  const hasTicketInfo = !!ticket_info;
  const cover = cover_image?.path;
  const price = ticket_info?.price || 0;
  const price_button_text = ticket_info?.price_button_text;
  const contentUrl = getContentUrl({ short_id, slug });
  const ticketUrl = `/tickets/${short_id}`;
  const { full: html } = sanitize({ raw: summary || '', textOnly: false });
  const { full: text } = sanitize({ raw: summary || '', textOnly: true });

  const { comingSoon } = inspectFlags(flag);
  const IsComingSoon = !!coming_soon || comingSoon;

  const getActorRole = () => {
    const roleNameToFind = 'کارگردان';
    let count = 0;
    const flatArray: { role: string; name: string }[] = [];
    if (artists) artists.forEach((artist) => {
      if (artist?.roles.includes(roleNameToFind)) {
        count += 1;
        if (artist.person && artist.person.name) {
          flatArray.push({ role: roleNameToFind, name: artist.person.name });
        }
      }
    });
    return count > 0 ? <Flex>
      <Text variant="body2" color="weak" pb={2}>
        {count > 1 ? 'کارگردانان:' : 'کارگردان:'}
        <Box as="span" ml={1} pb={2} />
      </Text>
      <Text variant="body2" weight="medium" pb={2}>
        {count === 1 ? flatArray[0]?.name : flatArray.map((obj) => obj?.name).join(', ')}
      </Text>
    </Flex> : <></>;
  };

  return (
    <Paper mb={{ xs: 3 }} borderRadius="medium" style={{ position: 'relative' }}>
      <Flex justify="flex-end">
        <div style={{ width: '100%', maxWidth: 960 }}>
          <ResponsiveCard
            cover={cover}
            orientation="horizontal"
          >
            <CinemaCardCover />
          </ResponsiveCard>
        </div>
      </Flex>
      <CinemaCardContent pt={{ xs: 4, sm: 7 }} pb={{ xs: 4, sm: 9 }} px={{ xs: 4, md: 3 }}>
        <div>
          <Text variant="h2" weight="medium" mb={{ xs: 1, md: 2 }}>
            {title}
          </Text>
          {artists && artists.length > 0 && getActorRole()}
          <Text as="p" variant="body2" className="desktop-only" style={{ maxWidth: 544 }}>
            <HtmlParser data={html} />
          </Text>
          <Ellipsis className="mobile-only" style={{ maxWidth: 480 }}>
            <HtmlParser data={text} />
          </Ellipsis>
        </div>
        <ActionButtons
          hasTicketInfo={hasTicketInfo}
          IsComingSoon={IsComingSoon}
          details={contentUrl} purchase={ticketUrl} price={price} short_id={short_id}
          price_button_text={price_button_text}
          conditions={conditions}
          userTicketInfo={userTicketInfo}
          isTapsiSSO={isTapsiSSO}
          loading={loading} />
      </CinemaCardContent>
    </Paper>
  );
};

const ResponsiveCard = styled(Card)<{ cover: string | undefined }>(({ cover, theme }) => ({
  backgroundImage: cover && `url(${cover}?w=1125)`,
  [theme.breakpoints.down('sm')]: {
    height: '300px',
  },
}));

type ActionButtonsProps = {
  details: string;
  purchase: string;
  price: number;
  className?: string;
  short_id: string,
  price_button_text?: string,
  IsComingSoon?: boolean,
  hasTicketInfo?: boolean,
  conditions: string | null,
  userTicketInfo?: ticketInfoType,
  isTapsiSSO?: boolean,
  loading?: boolean,
};

const ActionButtons = ({
  details, purchase, className, short_id,
  price_button_text, IsComingSoon, hasTicketInfo,
  conditions, userTicketInfo, isTapsiSSO = false, loading = false,
}: ActionButtonsProps) => {
  const { authorized } = useUser();
  const { query } = useRouter();
  const utmParams = ['utm_campaign', 'utm_source', 'utm_medium']
    .filter((key) => query[key])
    .map((key, i) => `${i === 0 ? '?' : '&'}${key}=${query[key]}`)
    .join('');

  // CTA used before we have an authenticated, entitled session (SSR + first paint, and logged-out
  // users): the "login & watch" button. Skipped for Tapsi SSO users (who can't purchase here) and
  // for contents without a ticket. This is also the default value of the ctaConditions state below.
  const getUnauthenticatedCTA = (): ICTA | undefined => {
    if (!hasTicketInfo || isTapsiSSO) return;
    return {
      variant: 'primaryB',
      cursor: 'pointer',
      text: __t('content.watch.custom', { customText: price_button_text as string }),
      as: Link,
      href: `/login?redirect=${purchase}${utmParams}`,
    };
  };

  const handleRenderCTA = (): ICTA | undefined => {
    if (conditions?.includes('age_restricted')) return;
    if (IsComingSoon) {
      return {
        variant: 'primaryB',
        cursor: 'default',
        text: __t('common.soon'),
        as: 'button',
        href: '#',
      };
    }

    if (!authorized) return getUnauthenticatedCTA();

    if (conditions && conditions.includes('playable') && conditions.includes('has_access') && userTicketInfo?.status === 'active') {
      return {
        variant: 'primaryA',
        cursor: 'pointer',
        text: __t('content.watch'),
        as: Link,
        href: `/play/${short_id}/p`,
        icon: <Icon cursor="pointer"><IconPlay /></Icon>,
        iconStart: true,
      };
    }

    if (userTicketInfo?.status === 'pending') {
      return {
        variant: 'primaryB',
        cursor: 'default',
        text: __t('content.watch.start.cinema', { customText: userTicketInfo?.activation_date_time_text as string }),
        as: 'button',
        href: '#',
      };
    }

    if (conditions !== 'none' && price_button_text && purchase) {
      if (isTapsiSSO) return;
      return {
        variant: 'primaryB',
        cursor: 'pointer',
        text: __t('content.watch.custom', { customText: price_button_text as string }),
        as: Link,
        href: `${purchase}${utmParams}`,
      };
    }
  };

  // Held in state so the default (the logged-out CTA) renders on SSR / first paint, then we recompute
  // it whenever the auth state or the fetched entitlement (conditions / ticket / SSO flag) changes.
  const [ctaConditions, setCtaConditions] = useState<ICTA | undefined>(getUnauthenticatedCTA);

  useEffect(() => {
    setCtaConditions(handleRenderCTA());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorized, conditions, userTicketInfo, isTapsiSSO]);
  return <ActionButtonsContainer>
    <Flex direction={{ xs: 'column', md: 'row' }} className={className}>
      {ctaConditions && <Button
        mt={{ xs: 2 }}
        ml={{ md: 3 }}
        style={{ cursor: ctaConditions?.cursor, minWidth: loading ? '178px' : '' }}
        variant={ctaConditions?.variant}
        href={ctaConditions?.href}
        as={ctaConditions?.as}
        iconStart={ctaConditions?.iconStart}
        disabled={loading}
      >
        {loading ? <Spinner />
          : <>
            {ctaConditions?.icon}
            {ctaConditions?.text}
          </>}
      </Button>}
      <Button href={details} as={Link} iconStart variant="secondary" mt={{ xs: 2 }} ml={{ md: 3 }}>
        <Icon cursor="pointer"><IconInfo /></Icon>
        {__t('common.more_info')}
      </Button>
    </Flex>
  </ActionButtonsContainer>;
};

const ActionButtonsContainer = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    position: 'absolute',
    bottom: theme.spacing(4),
    right: theme.spacing(3),
  },
}));

const CinemaCardContent = styled(Box)({
  position: 'absolute',
  top: 0,
  right: 0,
  maxWidth: '100%',
});

const CinemaCardCover = styled(CardBody)(({ theme }) => {
  const bg = theme.palette.background[700][0];
  return {
    opacity: 1,
    background: 'transparent',
    backgroundImage: `linear-gradient(
      to left,
      ${bg},
      ${theme.rgba(bg, 0.85)},
      ${theme.rgba(bg, 0.25)},
      rgba(0,0,0,0))
    `,
  };
});

export const getServerSideProps = async ({ req, res }) => {
  xIPCountry({ req, res });

  try {
    const { data } = await fetchCinemaOnline();
    return {
      props: data,
    };
  } catch (err) {
    Sentry.withScope((scope) => {
      scope.setTag('Status', err.response.status);
      scope.setTag('StatusText', err.response.statusText);
      scope.setTag('Url', err.response.config.url);
      Sentry.captureException(err.response);
    });
  }
};

export default CinemaOnlinePage;

type ICTA = {
  variant: ButtonVariant,
  cursor: 'pointer' | 'default',
  text: string,
  as: ElementType<any>,
  href?: string,
  // eslint-disable-next-line no-undef
  icon?: JSX.Element,
  iconStart?: boolean,
}
